// @flow
import * as React from 'react';
import { connect } from 'react-redux';
import BoardCollage from 'app/common/react/components/profile/ProfileBoardCard/BoardCollage';
import BoardFollowButton from 'app/common/react/components/BoardCard/BoardFollowButton';
import i18n from 'app/common/lib/i18n';
import NavigateLink from 'app/common/react/ui/NavigateLink';
import PinAndSectionCount from 'app/common/react/components/BoardCard/PinAndSectionCount';
import ProgressBar from 'app/common/react/components/ProgressBar/ProgressBar';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import Toast from 'app/common/react/ui/Toast';
import type { State as ReduxState } from 'app/common/redux/reducers/types';
import withShowToast from 'app/common/lib/react/withShowToast';
import { Board } from 'app/www/bundleLoaders';
import { compose } from 'redux';
import { MultiTreatmentExp } from 'app/common/lib/experiment';
import { safeFormat } from 'app/common/lib/StringUtil';
import type {
  ImageType,
  ProfileBoardType,
} from 'app/common/react/components/profile/ProfileBoardCard/commonTypes';
import type { Invite, InviteUser } from 'app/common/react/components/BoardCollab/types';
import {
  MAX_AVATARS_SHOWN,
  type ProfileBoardCardLayout,
} from 'app/common/react/components/profile/ProfileBoardCard/layout';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';
import { Avatar, Box, Button, Card, GroupAvatar, Icon, IconButton, Image, Text } from 'gestalt';

type OwnProps = {
  board: ProfileBoardType,
  canEdit: boolean,
  coverImageUrl: string,
  images: {
    '170x': ?Array<ImageType>,
  },
  invite?: Invite,
  isMergeTarget?: boolean,
  isOwnProfile?: boolean,
  isReordering?: boolean,
  isUserBlocked?: boolean,
  layout: 'list' | ProfileBoardCardLayout,
  onDeclineRequest?: () => void,
  onEditClick: (event: SyntheticMouseEvent<>) => void,
  progress: ?number,
  showSecretIcon: boolean,
  showToast: (Toast: React.ComponentType<any>) => void,
  subtitle: ?React.Element<any>,
  toastImageUrl: string,
};

type ReduxProps = {
  isUserBlocked?: boolean,
  reduxBoard?: ProfileBoardType,
};

type HOCProps = {
  requestContext: RequestContextType,
};

type State = {
  isActive: boolean,
  isBoardInviteAccepted: boolean,
};

type Props = OwnProps & ReduxProps & HOCProps;

const THUMB_SIZE_KEY = '170x';

export const LockIcon = () => (
  <Box marginEnd={2}>
    <Icon
      accessibilityLabel={i18n._(
        'Secret board',
        'Accessibility label for icon indicating a secret board',
      )}
      color="darkGray"
      icon="lock"
      inline
    />
  </Box>
);

const getTrueImageSignature = (coverPin: { image_signature: ?string, image_url: string }) => {
  // we dont always get back the image signature of a cover pin
  // so let's look for it so we can check if the cover pin
  // is in the list of board thumbs;
  // we remove the cover pin from board thumbs, so that we dont see
  // it duplicated in the wide view board rep
  const splitImageUrl = coverPin.image_url.split('/');
  return !coverPin.image_signature
    ? splitImageUrl[splitImageUrl.length - 1].split('.')[0]
    : coverPin.image_signature;
};

export class ProfileBoardCard extends React.Component<Props, State> {
  state = {
    isBoardInviteAccepted: false,
    isActive: false,
  };

  UNSAFE_componentWillReceiveProps(nextProps: Props) {
    if (nextProps.isReordering) {
      this.setState({ isActive: false });
    }
  }

  getCoverPinPos = () => {
    if (!this.props.reduxBoard) {
      return {
        height: 0,
        width: 0,
        x: 0,
        y: 0,
      };
    }

    const {
      reduxBoard: {
        cover_pin: { size, crop },
      },
    } = this.props;

    return {
      width: (size && size[0]) || 0,
      height: (size && size[1]) || 0,
      x: (crop && crop[0]) || 0,
      y: (crop && crop[1]) || 0,
    };
  };

  getInviteeInfo(invitee: InviteUser) {
    const fullName = invitee.full_name;
    return safeFormat(i18n._('{{ fullName }} invited you to this board'), {
      fullName,
    });
  }

  handleInviteResponse = (isAccepted: boolean) => {
    const { invite, onDeclineRequest, showToast } = this.props;

    if (!invite) {
      return;
    }

    const { board = {}, invited_user = {} } = invite;

    const resourceOptions = {
      board_id: board.id,
      invited_user_id: invited_user.id,
    };
    const inviteResource = ResourceFactory.create('BoardInviteResource', resourceOptions);
    const action = isAccepted ? 'callUpdate' : 'callDelete';
    inviteResource[action]().then(() => {
      this.setState({
        isBoardInviteAccepted: isAccepted,
      });
      if (!isAccepted && onDeclineRequest) {
        onDeclineRequest();
        showToast(({ onHide }) => (
          <Toast
            onHide={onHide}
            text={i18n._(
              'Invitation declined',
              'text in toast after declined a board collaborator invite',
            )}
          />
        ));
      }
    });
  };

  handleMouseEnter = () => {
    const { isReordering } = this.props;
    this.setState({ isActive: !isReordering });
    Board.loader();
  };

  handleMouseLeave = () => {
    this.setState({ isActive: false });
  };

  gridView({ reduxBoard }: { reduxBoard: ProfileBoardType }) {
    const {
      canEdit,
      images,
      invite,
      isOwnProfile,
      layout,
      onEditClick,
      progress,
      showSecretIcon,
      subtitle,
      toastImageUrl,
      isUserBlocked,
      requestContext,
    } = this.props;

    const { isBoardInviteAccepted, isActive } = this.state;
    const {
      collaborating_users: collaboratingUsers = [],
      cover_pin,
      followed_by_me: isFollowedByMe,
      has_custom_cover,
      id,
      is_collaborative: isCollaborative,
      name,
      owner,
      owner: { id: ownerId },
      pin_count: pinCount,
      place_saves_count: placesCount,
      section_count: sectionCount,
      url,
    } = reduxBoard;

    const coverImagePos = (cover_pin &&
      cover_pin.crop &&
      cover_pin.image_size && {
        x: cover_pin.crop[0],
        y: cover_pin.crop[1],
        width: cover_pin.image_size[0],
        height: cover_pin.image_size[1],
      }) || { x: 0, y: 0, width: 0, height: 0 };

    let cover = cover_pin.image_url
      ? {
          isCustom: has_custom_cover,
          position: coverImagePos,
          url: cover_pin.image_url,
        }
      : undefined;

    const thumbImages = images[THUMB_SIZE_KEY] || [];

    let orderedBoardThumbs = [];
    if (cover && cover_pin) {
      const imageSignature = getTrueImageSignature(cover_pin);

      thumbImages.forEach((image) => {
        if (image.url.indexOf(imageSignature) === -1) {
          orderedBoardThumbs.push(image);
        }
      });
    } else {
      orderedBoardThumbs = thumbImages;
      if (!!invite && layout !== 'square' && thumbImages.length) {
        cover = {
          isCustom: has_custom_cover,
          position: coverImagePos,
          url: thumbImages[0].url,
        };
        orderedBoardThumbs = thumbImages.slice(1);
      }
    }

    const followButtonExp = new MultiTreatmentExp(
      requestContext,
      'web_remove_profile_board_follow',
    );

    const followButtonDisplayChecks =
      followButtonExp.check('enabled') || followButtonExp.check('employees')
        ? !canEdit && !isUserBlocked && isFollowedByMe
        : !canEdit && !isUserBlocked;

    const boardCollage =
      layout !== 'list' ? (
        <BoardCollage
          boardThumbs={orderedBoardThumbs}
          layout={layout}
          cover={cover}
          coverPin={cover_pin}
          name={name}
          coverImagePos={coverImagePos}
          hasCustomCover={has_custom_cover}
          isInvite={!!invite}
        />
      ) : null;

    return (
      <React.Fragment>
        <Box position="relative">
          {/* extra layout check to keep flow happy */}
          {!!invite ? <NavigateLink to={url}>{boardCollage}</NavigateLink> : boardCollage}
          {!invite &&
            isCollaborative && (
              <Box position="absolute" bottom left marginStart={2} marginBottom={-2}>
                <GroupAvatar
                  collaborators={[owner, ...collaboratingUsers]
                    .slice(0, MAX_AVATARS_SHOWN)
                    .map((person) => ({
                      name: person.full_name || person.username,
                      src: !person.is_default_image ? person.image_medium_url : undefined,
                    }))}
                  outline
                  size="md"
                />
              </Box>
            )}
          {!!invite && (
            <Box position="absolute" bottom left marginStart={2} marginBottom={-2}>
              <NavigateLink stopPropagation to={`/${invite.invited_by_user.username}/`}>
                <Avatar
                  outline
                  size="md"
                  src={invite.invited_by_user.image_medium_url}
                  name={invite.invited_by_user.username}
                />
              </NavigateLink>
            </Box>
          )}
        </Box>
        {typeof progress === 'number' ? (
          <Box marginBottom={-1}>
            <ProgressBar height={4} progress={progress} />
          </Box>
        ) : null}
        <Box display="flex" alignItems="end" paddingY={1}>
          {!!invite ? (
            <Box marginTop={1} paddingX={4} paddingY={2} flex="grow" overflow="hidden">
              <NavigateLink to={url}>
                {isBoardInviteAccepted ? (
                  <Text size="sm" truncate>
                    {i18n._(
                      'You’ve joined',
                      'Text shown when accept collaborator board invitations',
                    )}
                  </Text>
                ) : (
                  <Text size="sm" truncate>
                    {this.getInviteeInfo(invite.invited_by_user)}
                  </Text>
                )}
                <Text size="xl" truncate weight="bold">
                  {name}
                </Text>
              </NavigateLink>
            </Box>
          ) : (
            <Box padding={2} flex="grow" overflow="hidden">
              <Text size="xl" truncate weight="bold">
                {name}
              </Text>
              <Box display="flex" alignItems="center">
                {showSecretIcon && <LockIcon />}
                <PinAndSectionCount
                  {...(placesCount ? { placesCount } : {})}
                  pinCount={pinCount}
                  sectionCount={sectionCount}
                />
              </Box>
              {isOwnProfile && (
                <Text size="sm" truncate>
                  {subtitle}
                </Text>
              )}
            </Box>
          )}
          {canEdit &&
            isActive && (
              <Box flex="none" marginEnd={1}>
                <IconButton
                  icon="edit"
                  onClick={({ event }) => onEditClick(event)}
                  accessibilityLabel={i18n._('Edit')}
                />
              </Box>
            )}
          {!invite &&
            followButtonDisplayChecks && (
              <Box flex="none" paddingY={2}>
                <BoardFollowButton
                  boardId={id}
                  boardName={name}
                  boardImage={toastImageUrl}
                  boardUrl={url}
                  isFollowed={isFollowedByMe}
                  ownerId={ownerId}
                />
              </Box>
            )}
        </Box>
        {!!invite &&
          (isBoardInviteAccepted ? (
            <Box display="flex" direction="row" paddingX={2}>
              <Box flex="grow" marginBottom={3} paddingX={1}>
                <NavigateLink to={url}>
                  <Button
                    color="gray"
                    textColor="darkGray"
                    text={i18n._('View board', 'view board after accepting collaborator invite')}
                  />
                </NavigateLink>
              </Box>
            </Box>
          ) : (
            <Box display="flex" direction="row" paddingX={2}>
              <Box column={6} marginBottom={3} paddingX={1}>
                <Button
                  color="gray"
                  onClick={() => this.handleInviteResponse(false)}
                  textColor="darkGray"
                  text={i18n._('Decline', 'decline board collaborator invite or request')}
                />
              </Box>
              <Box column={6} marginBottom={3} paddingX={1}>
                <Button
                  color="red"
                  onClick={() => this.handleInviteResponse(true)}
                  textColor="white"
                  text={i18n._('Accept', 'accept board invite')}
                />
              </Box>
            </Box>
          ))}
      </React.Fragment>
    );
  }

  listView({ reduxBoard }: { reduxBoard: ProfileBoardType }) {
    const { canEdit, onEditClick, showSecretIcon, subtitle } = this.props;

    const { isActive } = this.state;

    const {
      image_cover_url: imageCoverUrl,
      is_collaborative: isCollaborative,
      name,
      pin_count: pinCount,
      section_count: sectionCount,
    } = reduxBoard;

    const collaborativeIcon = isCollaborative && (
      <Box marginEnd={2} paddingX={1}>
        <Icon
          accessibilityLabel={i18n._('Group board', 'Accessibility label to indicate group boards')}
          color="darkGray"
          icon="people"
          size={16}
        />
      </Box>
    );

    return (
      <Box alignItems="center" display="flex" marginStart={-4} marginEnd={-4}>
        <Box column={9} display="flex" paddingX={4}>
          <Box display="flex" justifyContent="between" flex="grow" marginStart={-4} marginEnd={-4}>
            <Box paddingX={4} overflow="hidden">
              <Box display="flex" marginStart={-2} marginEnd={-2}>
                <Box flex="none" paddingX={2}>
                  <Box
                    dangerouslySetInlineStyle={{ __style: { borderRadius: 4 } }}
                    height={40}
                    overflow="hidden"
                    width={40}
                  >
                    {imageCoverUrl ? (
                      <Image
                        alt=""
                        fit="cover"
                        naturalHeight={1}
                        naturalWidth={1}
                        src={imageCoverUrl}
                      />
                    ) : (
                      <Box color="lightGray" height="100%" width="100%" />
                    )}
                  </Box>
                </Box>
                <Box
                  alignItems="center"
                  display="flex"
                  marginStart={-1}
                  marginEnd={-1}
                  overflow="hidden"
                  paddingX={2}
                >
                  <Box overflow="hidden" paddingX={1}>
                    <Text size="lg" truncate weight="bold">
                      {name}
                    </Text>
                  </Box>
                </Box>
              </Box>
            </Box>
            <Box paddingX={4} display="flex" alignItems="center" flex="none">
              {showSecretIcon && <LockIcon />}
              {collaborativeIcon}
              <PinAndSectionCount pinCount={pinCount} sectionCount={sectionCount} />
            </Box>
          </Box>
        </Box>
        <Box
          alignItems="center"
          column={3}
          display="flex"
          marginStart={-1}
          marginEnd={-1}
          paddingX={4}
          justifyContent="between"
        >
          {subtitle && (
            <Box overflow="hidden" paddingX={1}>
              <Text size="sm" __dangerouslyIncreaseLineHeight truncate>
                {subtitle}
              </Text>
            </Box>
          )}
          {canEdit &&
            isActive && (
              <Box flex="none" paddingX={1}>
                <IconButton
                  icon="edit"
                  onClick={({ event }) => onEditClick(event)}
                  accessibilityLabel={i18n._('Edit', 'Edit button accessibility label')}
                />
              </Box>
            )}
        </Box>
      </Box>
    );
  }

  render() {
    const { board, invite, isMergeTarget, layout, reduxBoard } = this.props;

    const { isActive } = this.state;

    if (invite && board) {
      return (
        <Card active={isActive || isMergeTarget}>
          <Box
            onMouseEnter={this.handleMouseEnter}
            onMouseLeave={this.handleMouseLeave}
            data-test-id={`board-${board.name}`}
          >
            {layout === 'list'
              ? this.listView({ reduxBoard: board })
              : this.gridView({ reduxBoard: board })}
          </Box>
        </Card>
      );
    }

    if (!reduxBoard) {
      return null;
    }

    const { name } = reduxBoard;

    return (
      <Card active={isActive || isMergeTarget}>
        <Box
          onMouseEnter={this.handleMouseEnter}
          onMouseLeave={this.handleMouseLeave}
          data-test-id={`board-${name}`}
        >
          {layout === 'list' ? this.listView({ reduxBoard }) : this.gridView({ reduxBoard })}
        </Box>
      </Card>
    );
  }
}

const mapStateToProps = (state: ReduxState, props: OwnProps): $Exact<ReduxProps> => {
  const userId = props.board && props.board.owner && props.board.owner.id;
  const user = state.users[userId];
  const id = props.board && props.board.id;
  const reduxBoard = state.boards && state.boards.content && state.boards.content[id];
  return {
    isUserBlocked: user && user.blocked_by_me,
    reduxBoard,
  };
};

export default compose(
  // $FlowFixMe Deprecated type. Using * types is not recommended! (deprecated-type)
  connect<*, OwnProps, _, _, _, _>(mapStateToProps),
  withShowToast,
  withRequestContext,
)(ProfileBoardCard);
